import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { EntitlementStoreSchema } from '../../billing/entitlement-store/entitlement-store';
import { FeeOfferSchema } from '../../billing/fee-payment/fee-payment';

/**
 * What stands between this member and the calendar, **stated by the server**
 * (D9a). The client draws exactly one of the states below and never derives
 * them: the fee policy is gender-scoped (U15), the ticket ledger is billing's,
 * and the cutoff is a setting — three facts the phone must not be trusted to
 * re-compose.
 *
 * - `stage` — `awaiting_evidence` while a reviewer has yet to accept the ID
 *   and the selfie (F4): nothing is asked of the member for the interview, not
 *   the fee and not a time, and a booking is refused. `open` once they have.
 *   Stated here rather than derived from the steps on the phone: whether the
 *   evidence gates the interview at all is a scoped setting.
 * - `feeDue` — a payment is needed before the next booking. `offer` says how;
 *   `null` with `feeDue` means the fee is switched on and no product carries it
 *   for this member's audience, which the screen reports as the platform's gap,
 *   not the member's. Never `true` while the stage is `awaiting_evidence`.
 * - `hasTicket` — a settled fee is held and unconsumed; the calendar is open.
 * - `storePurchasesRecorded` — how many purchases of this fee the platform has
 *   on record from the app stores for this member, in **any** status. The phone
 *   holds the store's own list of the member's purchases and compares the two:
 *   one more in the store than here is a payment the webhook has not delivered
 *   yet, and the phone must then wait rather than offer to sell the fee again.
 *   A count, not a list of transaction ids, because the id the store shows the
 *   phone and the one it sends the webhook are not promised to be the same
 *   string. Refunded rows are counted so a refund can never make the store look
 *   ahead; direct payments are not, because the store never saw them.
 * - `canReschedule` — the current appointment is still outside the cutoff, and
 *   the stage is `open`.
 */
export const INTERVIEW_STAGES = ['awaiting_evidence', 'open'] as const;

export const InterviewStageSchema = z.enum(INTERVIEW_STAGES);

export type InterviewStage = (typeof INTERVIEW_STAGES)[number];

export const InterviewAccessSchema = z
  .object({
    stage: InterviewStageSchema,
    feeEnabled: z.boolean(),
    feeDue: z.boolean(),
    hasTicket: z.boolean(),
    storePurchasesRecorded: z.number().int().nonnegative(),
    offer: FeeOfferSchema.nullable(),
    canReschedule: z.boolean(),
    rescheduleCutoffMinutes: z.number().int().nonnegative(),
  })
  .strict();

export type InterviewAccess = z.infer<typeof InterviewAccessSchema>;

/**
 * The staff view of the same fact: whether this member has paid, through which
 * door, and whether the ticket was spent — what the case page's fee row draws.
 */
export const INTERVIEW_FEE_STATES = [
  'not_required',
  /** The fee applies, but the evidence is not yet accepted — payment is not asked for (F4). */
  'awaiting_evidence',
  'awaiting_payment',
  'ticket_held',
  'consumed',
] as const;

export const InterviewFeeStateSchema = z.enum(INTERVIEW_FEE_STATES);

export type InterviewFeeState = (typeof INTERVIEW_FEE_STATES)[number];

export const InterviewFeeStandingSchema = z
  .object({
    state: InterviewFeeStateSchema,
    /** The most recent ticket, when there is one. */
    ticket: z
      .object({
        id: UuidSchema,
        store: EntitlementStoreSchema,
        settledAt: z.iso.datetime(),
        consumedAt: z.iso.datetime().nullable(),
      })
      .strict()
      .nullable(),
    /**
     * How the member would pay, **only while payment is awaited** — what the case
     * page prefills the direct-payment desk with (D9b2). `null` in every other
     * state, and `null` while awaiting when no active fee product carries the
     * purpose for this member's audience: the catalogue's gap, not the member's.
     */
    offer: FeeOfferSchema.nullable(),
  })
  .strict();

export type InterviewFeeStanding = z.infer<typeof InterviewFeeStandingSchema>;

/**
 * A ticket's life: minted `open` when billing settles the fee, `consumed` by
 * the booking that spent it, `voided` when the payment behind it was refunded
 * before it was spent. A consumed ticket is never voided — the interview it
 * bought stands, and the refund is a matter between staff and the member.
 */
export const INTERVIEW_FEE_TICKET_STATUSES = ['open', 'consumed', 'voided'] as const;

export type InterviewFeeTicketStatus = (typeof INTERVIEW_FEE_TICKET_STATUSES)[number];
